/**
 * Neighborhood Intelligence — admin API client.
 *
 * Backs the admin "Neighborhoods" section: an index of areas with headline
 * KPIs, and a per-area intelligence dashboard (inventory, engagement, demand,
 * agents, trends, and the area's news). All endpoints are admin-gated.
 */

import { apiRequest } from '../utils/apiClient';
import { getAuthToken } from '../utils/authToken';

function getToken(): string {
  return getAuthToken();
}

export interface NeighborhoodListItem {
  areaId: string;
  nameEn: string;
  nameAr: string;
  slug: string;
  cityNameEn: string | null;
  cityNameAr: string | null;
  latitude: number | null;
  longitude: number | null;
  activeListings: number;
  propertyViews: number;
  areaViews: number;
  newsCount: number;
  activityScore: number;
}

export interface KeyCount { key: string; count: number }

export interface NeighborhoodAgent {
  id: string;
  name: string;
  avatarUrl: string | null;
  avgRating: number;
  totalReviews: number;
  badge: string | null;
  listingsInArea: number;
}

export interface AreaNewsItem {
  id: string;
  cityId: string;
  areaId?: string | null;
  neighborhood?: string | null;
  titleEn: string;
  titleAr: string;
  bodyEn?: string | null;
  bodyAr?: string | null;
  category: string;
  impact: string;
  severity?: string | null;
  status: string;
  startDate: string;
  endDate?: string | null;
  source?: string | null;
  sourceUrl?: string | null;
}

export interface NeighborhoodDashboard {
  area: {
    id: string; nameEn: string; nameAr: string; slug: string;
    cityId: string;
    cityNameEn: string | null; cityNameAr: string | null;
    latitude: number | null; longitude: number | null;
    hasBoundary: boolean;
    boundary: import('./neighborhoodMapService').GeoJSONGeometry | null;
    boundarySource: string | null;
  };
  period: string;
  activityScore: number;
  sales: {
    soldAllTime: number; rentedAllTime: number;
    soldInPeriod: number; rentedInPeriod: number;
    sellingRate: number;
    soldByCategory: KeyCount[];
  };
  inventory: {
    totalActive: number;
    byType: KeyCount[];
    byCategory: KeyCount[];
    byListingType: KeyCount[];
    avgPrice: number;
  };
  engagement: {
    propertyViewsAllTime: number;
    propertyViewsPeriod: number;
    favorites: number;
    inquiries: number;
    areaCardViews: number;
    areaUniqueViewers: number;
  };
  demand: {
    searches: number;
    conversion: { inquiries: number; favorites: number; viewings: number };
  };
  demandSupply: { category: string; activeListings: number; viewsInPeriod: number; viewsPerListing: number }[];
  agents: NeighborhoodAgent[];
  trends: {
    newListingsByMonth: { month: string; count: number }[];
    viewsByDay: { day: string; count: number }[];
    searchesByMonth: { month: string; count: number }[];
  };
  news: AreaNewsItem[];
}

export type NeighborhoodPeriod = '7d' | '30d' | '90d' | '1y';

export async function fetchNeighborhoods(params: {
  cityId?: string;
  search?: string;
  period?: NeighborhoodPeriod;
  sort?: string;
  page?: number;
  pageSize?: number;
}): Promise<{ data: NeighborhoodListItem[]; meta: { page: number; pageSize: number; total: number; totalPages: number } }> {
  const q = new URLSearchParams();
  if (params.cityId) q.set('cityId', params.cityId);
  if (params.search) q.set('search', params.search);
  if (params.period) q.set('period', params.period);
  if (params.sort) q.set('sort', params.sort);
  q.set('page', String(params.page ?? 1));
  q.set('pageSize', String(params.pageSize ?? 20));
  return apiRequest(`/admin/neighborhoods?${q.toString()}`, { token: getToken() });
}

export async function fetchNeighborhood(areaId: string, period: NeighborhoodPeriod): Promise<{ data: NeighborhoodDashboard }> {
  return apiRequest(`/admin/neighborhoods/${areaId}?period=${period}`, { token: getToken() });
}

// --- Area news CRUD (reuses the existing admin area-news endpoints) ---------

export interface SaveAreaNewsInput {
  cityId: string;
  areaId?: string | null;
  neighborhood?: string | null;
  titleEn: string;
  titleAr: string;
  bodyEn?: string | null;
  bodyAr?: string | null;
  category: string;
  impact: string;
  severity?: string | null;
  startDate: string;
  endDate?: string | null;
  status?: string;
  source?: string | null;
  sourceUrl?: string | null;
}

export async function createAreaNews(input: SaveAreaNewsInput): Promise<{ item: AreaNewsItem }> {
  return apiRequest('/admin/area-news', { method: 'POST', body: input, token: getToken() });
}

export async function updateAreaNews(id: string, input: Partial<SaveAreaNewsInput>): Promise<{ item: AreaNewsItem }> {
  return apiRequest(`/admin/area-news/${id}`, { method: 'PUT', body: input, token: getToken() });
}

export async function deleteAreaNews(id: string): Promise<void> {
  return apiRequest(`/admin/area-news/${id}`, { method: 'DELETE', token: getToken() });
}

/** Authed CSV download (export endpoint returns text/csv, not JSON). */
export async function downloadNeighborhoodCsv(areaId: string, period: NeighborhoodPeriod, filename: string): Promise<void> {
  const csv = await apiRequest<string>(`/admin/neighborhoods/${areaId}/export?period=${period}`, { token: getToken() });
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const NEWS_CATEGORIES = ['CONSTRUCTION', 'INFRASTRUCTURE', 'TRANSPORT', 'SERVICES', 'SAFETY', 'EVENT', 'ENVIRONMENT', 'REGULATORY', 'OTHER'];
export const NEWS_IMPACTS = ['POSITIVE', 'NEGATIVE', 'NEUTRAL', 'MIXED'];
export const NEWS_SEVERITIES = ['LOW', 'MEDIUM', 'HIGH'];
export const NEWS_STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED'];
